// The talent test platform reads Vite env and exposes the backend origin + the
// stub talent identity. Unlike admin it uses Pusher (live STERLING chat), so it
// also builds a PusherChannelConfig. The "current" talent is chosen at runtime
// (the tester simulates different talents), so the channel + auth headers are
// FACTORIES of a talent id rather than build-time constants.
import type { PusherChannelConfig } from "@recruiter/shared";

const env = import.meta.env;

// Optional seed: a fixed talent to simulate on first load. Empty by default —
// the tester picks/types which talent (a shortlist id) to act as. Persisted to
// localStorage thereafter (see lib/seller-session.ts).
export const SEED_SELLER_ID = env.VITE_DEV_SELLER_ID ?? "";

// Backend origin prefix. Local dev: the API's absolute origin (the SPA runs on its own
// port). Cloud: empty (the deploys set VITE_API_BASE_URL=""), because the load balancer
// routes this host's /api/* to the API, so every call is same-origin.
export const SELLER_API_BASE = env.VITE_API_BASE_URL ?? "http://localhost:8200";

// Fraction of page loads that report RUM (Core Web Vitals + client errors),
// decided once per load in initRum. Default 1; dial down per env via VITE_RUM_SAMPLE.
// RUM flushes via keepalive fetch to SELLER_API_BASE (see main.tsx).
export const RUM_SAMPLE = Number(env.VITE_RUM_SAMPLE ?? "1");

export const PUSHER_AUTH_ENDPOINT = `${SELLER_API_BASE}/api/pusher/auth`;

/** Stub identity header the talent SPA sends (real Fiverr auth later). */
export function sellerAuthHeaders(sellerId: string): Record<string, string> {
  return sellerId ? { "x-seller-id": sellerId } : {};
}

/** The talent's private Pusher channel — mirrors backend `channel_for_seller`. */
export function sellerChannel(sellerId: string): string {
  return `private-seller-${sellerId}`;
}

/** Base Pusher transport config; `authHeaders` is layered per simulated talent. */
export function basePusherConfig(sellerId: string): PusherChannelConfig {
  return {
    key: env.VITE_PUSHER_KEY ?? "local-key",
    host: env.VITE_PUSHER_HOST ?? "localhost",
    port: Number(env.VITE_PUSHER_PORT ?? "6201"),
    useTls: (env.VITE_PUSHER_USE_TLS ?? "false") === "true",
    cluster: env.VITE_PUSHER_CLUSTER ?? "mt1",
    authEndpoint: PUSHER_AUTH_ENDPOINT,
    authHeaders: sellerAuthHeaders(sellerId),
    // A tester's backgrounded tab awaits STERLING's async replies with no
    // reconcile-on-return path (the inbox folds live events only) — keep the
    // socket alive while hidden. The test platform's few tabs are negligible
    // against the quota the buyer-side idle-disconnect protects.
    idleDisconnectMs: 0,
  };
}
